import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createCommentVNode as _createCommentVNode } from "vue"

import { computed } from 'vue';
// @ts-ignore
import { watchEffect } from 'vue';
import { usePermissions } from '@/composables/usePermissions';
import type { PermissionType } from '@/services/permission.service';

import { useRoute } from 'vue-router'
interface Props {
  // Single permission check
  permission?: PermissionType | null;

  // Multiple permissions check (any)
  anyPermissions?: PermissionType[];

  // Multiple permissions check (all)
  allPermissions?: PermissionType[];

  // Require authentication
  requireAuth?: boolean;
  // Optional project id to check project-scoped permissions. If omitted the guard will
  // try to read `route.params.projectId` when available.
  projectId?: string | number | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PermissionGuard',
  props: {
    permission: { default: null },
    anyPermissions: { default: () => [] },
    allPermissions: { default: () => [] },
    requireAuth: { type: Boolean, default: true },
    projectId: { default: null }
  },
  emits: ["returnToProject"],
  setup(__props: any) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;

// Define emits to prevent warnings about extraneous event listeners


const {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasPermissionForProject,
  hasAnyPermissionForProject,
  hasAllPermissionsForProject,
  isAuthenticated
} = usePermissions();

// route may provide projectId when guard used inside project views
// @ts-ignore
const route = useRoute()

const hasAccess = computed(() => {
  // Check authentication requirement
  if (props.requireAuth && !isAuthenticated.value) {
    return false;
  }

  // If no permissions specified but auth is required, check if authenticated
  if (props.requireAuth &&
      !props.permission &&
      props.anyPermissions.length === 0 &&
      props.allPermissions.length === 0) {
    return isAuthenticated.value;
  }
  
  // Determine effective projectId, prefer prop then route param
  const effectiveProjectId = props.projectId ?? (route && (route.params as any).projectId ? (route.params as any).projectId : undefined)

  // Check single permission
  if (props.permission) {
    if (effectiveProjectId !== undefined && effectiveProjectId !== null) {
      return hasPermissionForProject(props.permission, effectiveProjectId).value;
    }
    return hasPermission(props.permission).value;
  }

  // Check any permissions
  if (props.anyPermissions.length > 0) {
    if (effectiveProjectId !== undefined && effectiveProjectId !== null) {
      return hasAnyPermissionForProject(props.anyPermissions, effectiveProjectId).value;
    }
    return hasAnyPermission(props.anyPermissions).value;
  }

  // Check all permissions
  if (props.allPermissions.length > 0) {
    if (effectiveProjectId !== undefined && effectiveProjectId !== null) {
      return hasAllPermissionsForProject(props.allPermissions, effectiveProjectId).value;
    }
    return hasAllPermissions(props.allPermissions).value;
  }

  // Default: allow access if no specific permissions are required
  return true;
});

// Debug logging in development
if (process.env.NODE_ENV === 'development') {
  watchEffect(() => {
    if (props.permission || props.anyPermissions.length > 0 || props.allPermissions.length > 0) {
      console.debug('PermissionGuard:', {
        hasAccess: hasAccess.value,
        permission: props.permission,
        anyPermissions: props.anyPermissions,
        allPermissions: props.allPermissions,
        isAuthenticated: isAuthenticated.value
      });
    }
  });
}

return (_ctx: any,_cache: any) => {
  return (hasAccess.value)
    ? _renderSlot(_ctx.$slots, "default", { key: 0 })
    : _createCommentVNode("", true)
}
}

})